import range from './';
import tacHelpers from '../../tacHelpers';
import productService from '../../../../services/products';
import { ITEMS } from '../../../../constants';

function getSectionsPreparedForUpdate(superSection, movedItem = null) {
  const sectionsSorted = superSection
    .filter(product => productService.isSection(product))
    .sort((a, b) => b.x - a.x)
    .filter(Boolean);

  return sectionsSorted.length ? sectionsSorted : [];
}

function replaceSectionsTransformedByMerge(superSection) {
  return superSection
    .filter(product => productService.isSection(product))
    .sort((a, b) => a.x - b.x)
    .map((section, index, allSections) => {
      if (
        allSections[index - 1]?.height > section.height &&
        allSections[index + 1]?.height > section.height
      ) {
        // This section has higher sections on both sides,
        // thereby changing its height
        const newHeight = [allSections[index - 1], allSections[index + 1]].sort(
          (a, b) => a.height - b.height
        )[0].height;

        // New height is equal to the lowest of the enclosing neighbors
        const replacementProduct = productService.getFit(section, {
          height: newHeight,
        });

        const newSection = tacHelpers.getSwitchableItem(
          section,
          replacementProduct
        );

        return newSection;
      }
      return section;
    });
}

/**
 * Splits an array of sections into two arrays of sections based on whether they
 * are located to the left or to the right of a specific section (dividingSection)
 * and returns an array of the resulting left/right arrays.
 * dividingSection may, but does not have to, be present in the input array. Even if
 * present, it will not be included neither in the left nor right arrays, since its x
 * value is neither less than nor greater than its own x value.
 * @param {Array} superSection The input array of sections.
 * @param {object} dividingSection The dividing section to compare to.
 * @returns {Array} The array containing the resulting left and right arrays.
 */
function splitSuperSectionIntoLeftAndRightParts(superSection, dividingSection) {
  const superSectionParts = [];

  const left = superSection.filter(section => section.x < dividingSection.x);
  const right = superSection.filter(section => section.x > dividingSection.x);
  superSectionParts.push(left, right);

  return superSectionParts;
}

/**
 * Gets the caused updates and removals in the sections due to a specific move
 * @param {*} tac
 * @param {*} triggerItem The moved item causing the changes
 * @param {*} isPersistent False if the user is still dragging
 * @returns {Object}
 */
const getSectionChanges = (tac, triggerItem, isPersistent) => {
  let sectionsToUpdate = [];

  const tacItem = tacHelpers.getItem(tac, triggerItem.itemid);
  let currentSuperSection = tacItem && range.findSuperSection(tac, tacItem, []);

  if (currentSuperSection?.length > 1) {
    if (isPersistent) {
      currentSuperSection =
        replaceSectionsTransformedByMerge(currentSuperSection);
    }
    sectionsToUpdate.push(...getSectionsPreparedForUpdate(currentSuperSection));
  }

  const oldSuperSection = range
    .findSuperSection(tac, triggerItem, [])
    .filter(member => {
      return (
        !currentSuperSection ||
        !currentSuperSection.find(
          candidate => candidate.itemid === member.itemid
        )
      );
    });

  const superSectionParts = oldSuperSection
    ? splitSuperSectionIntoLeftAndRightParts(oldSuperSection, triggerItem)
    : [];
  superSectionParts.forEach(superSectionPart => {
    if (superSectionPart.length) {
      sectionsToUpdate.push(
        ...getSectionsPreparedForUpdate(superSectionPart, triggerItem)
      );
    }
  });

  if (!tacItem) {
    sectionsToUpdate = sectionsToUpdate.filter(
      section => section.itemid !== triggerItem.itemid
    );
  }

  return {
    updated: sectionsToUpdate,
    removed: sectionsToUpdate.flatMap(section => {
      // Remove all side panels, posts and cross braces in the sections staged for update,
      // so we can re-assemble them according to the new setup
      return section.items.filter(item =>
        productService.isType(item, ['side-panel', 'post', 'cross-brace'])
      );
    }),
  };
};

/**
 * Gets all the dependency changes caused by a change in the TAC
 * @param {*} tac
 * @param {*} options
 * @returns {Object}
 */

function getDependencyDiff(tac, options = {}) {
  const { isPersistent, triggerItem, fromTacRemoveItem } = options;

  let diff = { added: [], updated: [], removed: [], untouched: [] };

  if (triggerItem && productService.isSection(triggerItem)) {
    diff = { ...diff, ...getSectionChanges(tac, triggerItem, isPersistent) };
  } else if (
    triggerItem &&
    productService.isType(triggerItem, ITEMS.SHELF_DRAWER)
  ) {
    const allItems = tac.items.reduce(
      (acc, curr) => [...acc, ...curr.items],
      []
    );
    const isShelfDrawer = item =>
      productService.isType(item, ITEMS.SHELF_DRAWER);
    if (!fromTacRemoveItem) {
      const shelfDrawersToUpdate = allItems.filter(isShelfDrawer).map(item => {
        return {
          ...item,
          parentRef: tacHelpers.getParent(tac, item).itemid,
        };
      });
      diff = {
        ...diff,
        updated: shelfDrawersToUpdate,
      };
    } else {
      const isShelf = item => productService.isType(item, ITEMS.SHELF);
      const isShelfDrawerWithoutShelf = shelfDrawer =>
        !shelfDrawer.items.find(item => isShelf(item));
      const shelfDrawersWithoutShelves = allItems
        .filter(isShelfDrawer)
        .filter(isShelfDrawerWithoutShelf)
        .map(item => {
          return {
            ...item,
            parentRef: tacHelpers.getParent(tac, item).itemid,
          };
        });
      diff = {
        ...diff,
        updated: [...shelfDrawersWithoutShelves],
      };
    }
  }
  return diff;
}

export default { getDependencyDiff };

export { getDependencyDiff };
